import LocalStorageHelper from "models/helper/LocalStorageHelper";

const DRAFT_DB_NAME = "trebble-browser-recording-drafts";
const DRAFT_DB_VERSION = 1;
const DRAFT_MANIFEST_STORE_NAME = "recordingDraftManifest";
const DRAFT_MANIFEST_VERSION = 2;
const RECOVERY_PROMPT_DISMISSAL_TTL_IN_MS = 30 * 60 * 1000;
const RECOVERABLE_DRAFT_MAX_AGE_IN_MS = 30 * 24 * 60 * 60 * 1000;
const COMPLETED_DRAFT_LOCAL_BINARY_RETENTION_IN_MS = 7 * 24 * 60 * 60 * 1000;
const DISCARDED_DRAFT_TOMBSTONE_RETENTION_IN_MS = 30 * 24 * 60 * 60 * 1000;
const LOW_STORAGE_WARNING_USAGE_RATIO = 0.85;
const LOW_STORAGE_CRITICAL_USAGE_RATIO = 0.95;
const LOW_STORAGE_WARNING_AVAILABLE_BYTES = 100 * 1024 * 1024;
const WORKER_REQUEST_TIMEOUT_IN_MS = 10000;

const STORAGE_BACKENDS = {
    OPFS: "opfs",
    INDEXED_DB: "indexeddb",
    MEMORY: "memory",
    CLEARED: "cleared"
};

const STATUSES = {
    RECORDING: "recording",
    STOPPING: "stopping",
    STOPPED_READY_TO_UPLOAD: "stopped_ready_to_upload",
    UPLOADING: "uploading",
    UPLOADED_UNREGISTERED: "uploaded_unregistered",
    REGISTERED_UNTRANSCRIBED: "registered_untranscribed",
    TRANSCRIBING: "transcribing",
    COMPLETED: "completed",
    FAILED_RECOVERABLE: "failed_recoverable",
    DISCARDED: "discarded"
};

const STATUS_ORDER = [
    STATUSES.RECORDING,
    STATUSES.STOPPING,
    STATUSES.STOPPED_READY_TO_UPLOAD,
    STATUSES.UPLOADING,
    STATUSES.UPLOADED_UNREGISTERED,
    STATUSES.REGISTERED_UNTRANSCRIBED,
    STATUSES.TRANSCRIBING,
    STATUSES.COMPLETED,
    STATUSES.FAILED_RECOVERABLE,
    STATUSES.DISCARDED
];

const RECOVERABLE_STATUSES = [
    STATUSES.RECORDING,
    STATUSES.STOPPING,
    STATUSES.STOPPED_READY_TO_UPLOAD,
    STATUSES.UPLOADING,
    STATUSES.UPLOADED_UNREGISTERED,
    STATUSES.REGISTERED_UNTRANSCRIBED,
    STATUSES.TRANSCRIBING,
    STATUSES.FAILED_RECOVERABLE
];

const USER_VISIBLE_RECOVERABLE_STATUSES = [
    STATUSES.STOPPED_READY_TO_UPLOAD,
    STATUSES.UPLOADING,
    STATUSES.UPLOADED_UNREGISTERED,
    STATUSES.REGISTERED_UNTRANSCRIBED,
    STATUSES.TRANSCRIBING,
    STATUSES.FAILED_RECOVERABLE
];

const CLEANUP_ELIGIBLE_STATUSES = [
    STATUSES.DISCARDED,
    STATUSES.COMPLETED
];

const NON_DESTRUCTIVE_REMOTE_CHECKPOINT_STATUSES = [
    STATUSES.UPLOADED_UNREGISTERED,
    STATUSES.REGISTERED_UNTRANSCRIBED,
    STATUSES.TRANSCRIBING,
    STATUSES.COMPLETED
];

const buildStatusRank = function(status) {
    const index = STATUS_ORDER.indexOf(status);
    return index >= 0 ? index : -1;
};

const isStatusAtOrBeyond = function(status, minimumStatus) {
    return buildStatusRank(status) >= buildStatusRank(minimumStatus);
};

const getHttpFailureCategory = function(httpStatus) {
    const normalizedStatus = Number(httpStatus);
    if (!normalizedStatus) {
        return null;
    }

    if (normalizedStatus === 429) {
        return "rate_limited";
    }

    if (normalizedStatus === 401 || normalizedStatus === 403) {
        return "auth_error";
    }

    if (normalizedStatus >= 500) {
        return "server_error";
    }

    if (normalizedStatus >= 400) {
        return "client_error";
    }

    return null;
};

const isQuotaExceededError = function(error) {
    if (!error) {
        return false;
    }

    const errorName = error.name || "";
    const errorCode = error.code || "";
    const errorMessage = error.message || String(error);
    return errorName === "QuotaExceededError" ||
        errorName === "NS_ERROR_DOM_QUOTA_REACHED" ||
        errorCode === "QuotaExceededError" ||
        /\bquota\b/i.test(errorMessage);
};

const sanitizeError = function(error) {
    if (!error) {
        return null;
    }

    if (typeof error === "string") {
        return {
            category: /\bquota\b/i.test(error) ? "quota_exceeded" : "string_error",
            message: error
        };
    }

    const httpStatus = error.httpStatus || error.status || error.statusCode || (error.response && error.response.status) || null;
    const errorCode = error.code || error.errorCode || null;
    const quotaExceeded = isQuotaExceededError(error);

    return {
        category: quotaExceeded ? "quota_exceeded" : (error.category || getHttpFailureCategory(httpStatus) || error.name || errorCode || "unknown"),
        message: error.message || String(error),
        name: error.name || null,
        code: errorCode,
        httpStatus: httpStatus,
        quotaExceeded: quotaExceeded
    };
};

const getErrorCategory = function(error) {
    const normalizedError = sanitizeError(error);
    return normalizedError ? normalizedError.category : null;
};

const hasRecoverableLocalBinary = function(draft) {
    if (!draft || draft.localBinaryClearedAt) {
        return false;
    }

    if (!draft.storageBackend) {
        return false;
    }

    if (draft.storageBackend === STORAGE_BACKENDS.CLEARED || draft.storageBackend === STORAGE_BACKENDS.MEMORY) {
        return false;
    }

    const durableBytesStored = typeof draft.durableBytesStored === "number" ? draft.durableBytesStored : draft.bytesStored;
    return !!(durableBytesStored && durableBytesStored > 0);
};

const hasRemoteRecoveryCheckpoint = function(draft) {
    if (!draft) {
        return false;
    }

    if (NON_DESTRUCTIVE_REMOTE_CHECKPOINT_STATUSES.indexOf(draft.status) >= 0) {
        return true;
    }

    return !!(
        draft.remoteUploadUrl ||
        draft.remoteUploadKey ||
        draft.libraryAudioId ||
        draft.transcriptionId ||
        draft.registeredMedia
    );
};

const hasExplicitDurableCompletionMetadata = function(draft) {
    return !!(
        draft &&
        draft.status === STATUSES.COMPLETED &&
        draft.durableCompletion &&
        draft.durableCompletion.savedOutcome === true &&
        draft.durableCompletion.completedAt
    );
};

const isDraftExpiredForRecovery = function(draft, now) {
    if (!draft) {
        return true;
    }

    const currentTimestamp = typeof now === "number" ? now : nowTimestamp();
    if (draft.expiredAt && draft.expiredAt <= currentTimestamp) {
        return true;
    }

    if (draft.recoveryExpiresAt && draft.recoveryExpiresAt <= currentTimestamp) {
        return true;
    }

    const lastMeaningfulTimestamp = draft.updatedAt || draft.startedAt || draft.createdAt;
    return !!(lastMeaningfulTimestamp && currentTimestamp - lastMeaningfulTimestamp > RECOVERABLE_DRAFT_MAX_AGE_IN_MS);
};

const isExpiredTombstoneDraft = function(draft, now) {
    if (!draft || draft.expiredAt) {
        return false;
    }

    if (draft.status !== STATUSES.DISCARDED && draft.status !== STATUSES.COMPLETED) {
        return false;
    }

    if (hasRecoverableLocalBinary(draft)) {
        return false;
    }

    const currentTimestamp = typeof now === "number" ? now : nowTimestamp();
    const tombstoneTimestamp = draft.discardedAt || draft.completedAt || draft.cleanupEligibleAt || draft.updatedAt;
    return !!(tombstoneTimestamp && currentTimestamp - tombstoneTimestamp > DISCARDED_DRAFT_TOMBSTONE_RETENTION_IN_MS);
};

const shouldDisplayRecordingDraftForUser = function(draft, userId) {
    if (!draft || !userId) {
        return false;
    }

    return draft.userId === userId;
};

const isRecordingDraftRecoverable = function(draft) {
    if (!draft || RECOVERABLE_STATUSES.indexOf(draft.status) < 0) {
        return false;
    }

    return hasRecoverableLocalBinary(draft) || hasRecoverableMemoryBinary(draft) || hasRemoteRecoveryCheckpoint(draft);
};

const isRecordingDraftUserRecoverable = function(draft) {
    if (!draft || USER_VISIBLE_RECOVERABLE_STATUSES.indexOf(draft.status) < 0) {
        return false;
    }

    return hasRecoverableLocalBinary(draft) || hasRecoverableMemoryBinary(draft) || hasRemoteRecoveryCheckpoint(draft);
};

const canCleanupLocalBinaryForDraft = function(draft, now) {
    if (!draft || CLEANUP_ELIGIBLE_STATUSES.indexOf(draft.status) < 0) {
        return false;
    }

    const hasMemoryLocalBinary = !!(
        draft.storageBackend === STORAGE_BACKENDS.MEMORY &&
        !draft.localBinaryClearedAt &&
        draft.bytesStored &&
        draft.bytesStored > 0
    );
    if (!hasRecoverableLocalBinary(draft) && !hasMemoryLocalBinary) {
        return false;
    }

    if (draft.status === STATUSES.DISCARDED) {
        return true;
    }

    if (draft.status !== STATUSES.COMPLETED || !hasExplicitDurableCompletionMetadata(draft)) {
        return false;
    }

    const currentTimestamp = typeof now === "number" ? now : nowTimestamp();
    const retentionUntil = draft.durableCompletion.localBinaryRetentionUntil || draft.cleanupEligibleAt;
    return !!(retentionUntil && currentTimestamp >= retentionUntil);
};

const shouldSuppressDraftRecoveryPrompt = function(draft, now, dismissalTtlInMs) {
    if (!isRecordingDraftRecoverable(draft)) {
        return true;
    }

    if (draft.dismissedAt && now - draft.dismissedAt < dismissalTtlInMs) {
        return true;
    }

    return false;
};

const nowTimestamp = function() {
    return Date.now();
};

const buildQuotaStatusFromStorageEstimate = function(storageEstimate, now) {
    if (!storageEstimate || typeof storageEstimate.usage !== "number" || typeof storageEstimate.quota !== "number" || storageEstimate.quota <= 0) {
        return null;
    }

    const usage = Math.max(storageEstimate.usage, 0);
    const quota = storageEstimate.quota;
    const usageRatio = usage / quota;
    const availableBytes = Math.max(quota - usage, 0);
    let warningLevel = null;

    if (usageRatio >= LOW_STORAGE_CRITICAL_USAGE_RATIO || availableBytes === 0) {
        warningLevel = "critical";
    } else if (usageRatio >= LOW_STORAGE_WARNING_USAGE_RATIO || availableBytes <= LOW_STORAGE_WARNING_AVAILABLE_BYTES) {
        warningLevel = "warning";
    }

    return {
        usage: usage,
        quota: quota,
        usageRatio: usageRatio,
        availableBytes: availableBytes,
        warningLevel: warningLevel,
        capturedAt: typeof now === "number" ? now : nowTimestamp()
    };
};

const cloneDraft = function(draft) {
    return draft ? JSON.parse(JSON.stringify(draft)) : null;
};

const createObjectStoreIfNeeded = function(database) {
    if (!database.objectStoreNames.contains(DRAFT_MANIFEST_STORE_NAME)) {
        const objectStore = database.createObjectStore(DRAFT_MANIFEST_STORE_NAME, {
            keyPath: "recordingId"
        });
        objectStore.createIndex("userId", "userId", { unique: false });
        objectStore.createIndex("status", "status", { unique: false });
        objectStore.createIndex("updatedAt", "updatedAt", { unique: false });
    }
};

const browserRecordingBinaryMemoryChunksByRecordingId = Object.create(null);

const getMemoryChunksKeyForRecordingId = function(recordingId) {
    return String(recordingId || "__unknown_recording__");
};

const getMemoryChunksForRecordingId = function(recordingId) {
    const recordingKey = getMemoryChunksKeyForRecordingId(recordingId);
    browserRecordingBinaryMemoryChunksByRecordingId[recordingKey] = browserRecordingBinaryMemoryChunksByRecordingId[recordingKey] || [];
    return browserRecordingBinaryMemoryChunksByRecordingId[recordingKey];
};

const getExistingMemoryChunksForRecordingId = function(recordingId) {
    return browserRecordingBinaryMemoryChunksByRecordingId[getMemoryChunksKeyForRecordingId(recordingId)] || null;
};

const clearMemoryChunksForRecordingId = function(recordingId) {
    delete browserRecordingBinaryMemoryChunksByRecordingId[getMemoryChunksKeyForRecordingId(recordingId)];
};

const hasRecoverableMemoryBinary = function(draft) {
    const memoryChunks = draft && draft.storageBackend === STORAGE_BACKENDS.MEMORY ? getExistingMemoryChunksForRecordingId(draft.recordingId) : null;
    return !!(
        draft &&
        draft.storageBackend === STORAGE_BACKENDS.MEMORY &&
        !draft.localBinaryClearedAt &&
        draft.bytesStored &&
        draft.bytesStored > 0 &&
        memoryChunks &&
        memoryChunks.length > 0
    );
};

class BrowserRecordingBinaryStoreClient {
    constructor(options) {
        this._recordingId = options.recordingId;
        this._fileName = options.fileName;
        this._mimeType = options.mimeType;
        this._requestId = 0;
        this._pendingRequestIdToPromise = {};
        this._writeQueue = Promise.resolve();
        this._memoryChunks = null;
        this._memoryBytesWritten = 0;
        this._durableBytesWritten = 0;
        this._lastWorkerStorageBackend = null;
        this._memoryFallbackActive = false;
        this._memoryFallbackError = null;
        this._worker = null;
        this._workerFatalError = null;
        this._workerInitError = null;

        try {
            this._worker = new Worker(new URL("./workers/RecordedMediaPersistenceWorker.js", import.meta.url));
            this._worker.onmessage = this._onWorkerMessage.bind(this);
            this._worker.onerror = this._onWorkerError.bind(this);
        } catch (error) {
            this._worker = null;
            this._workerInitError = error;
        }
    }

    _getMemoryChunks() {
        this._memoryChunks = getMemoryChunksForRecordingId(this._recordingId);
        return this._memoryChunks;
    }

    _getMemoryBytesWritten() {
        return this._getMemoryChunks().reduce(function(total, chunk) {
            return total + (chunk && chunk.size ? chunk.size : 0);
        }, 0);
    }

    _onWorkerMessage(event) {
        const data = event.data || {};
        const requestId = data.requestId;
        const pendingPromise = this._pendingRequestIdToPromise[requestId];

        if (!pendingPromise) {
            return;
        }

        delete this._pendingRequestIdToPromise[requestId];
        if (pendingPromise.timerId) {
            clearTimeout(pendingPromise.timerId);
        }
        if (data.type === "error") {
            const errorData = data.error || {};
            const workerError = new Error(errorData.message || "Recording worker error");
            workerError.name = errorData.name || "RecordingWorkerError";
            workerError.stack = errorData.stack;
            this._writeQueue = Promise.resolve();
            pendingPromise.reject(workerError);
            return;
        }

        pendingPromise.resolve(data);
    }

    _onWorkerError(error) {
        const workerFailureError = error instanceof Error ? error : new Error((error && error.message) || "Recording worker failed");
        this._workerFatalError = workerFailureError;
        Object.keys(this._pendingRequestIdToPromise).forEach((requestId) => {
            const pendingPromise = this._pendingRequestIdToPromise[requestId];
            if (pendingPromise.timerId) {
                clearTimeout(pendingPromise.timerId);
            }
            pendingPromise.reject(workerFailureError);
            delete this._pendingRequestIdToPromise[requestId];
        });

        if (this._worker) {
            try {
                this._worker.terminate();
            } catch (_) {
                // Ignore worker teardown failures while degrading to memory storage.
            }
            this._worker.onmessage = null;
            this._worker.onerror = null;
            this._worker = null;
        }
    }

    _sendMessage(type, payload, transferableArray) {
        if (this._workerFatalError) {
            return Promise.reject(this._workerFatalError);
        }

        if (!this._worker) {
            return Promise.resolve(null);
        }

        return new Promise((resolve, reject) => {
            const requestId = ++this._requestId;
            const timerId = setTimeout(() => {
                const pendingPromise = this._pendingRequestIdToPromise[requestId];
                if (!pendingPromise) {
                    return;
                }

                this._onWorkerError(new Error("Recording worker request timed out"));
            }, WORKER_REQUEST_TIMEOUT_IN_MS);
            this._pendingRequestIdToPromise[requestId] = {
                resolve,
                reject,
                timerId
            };
            try {
                this._worker.postMessage(Object.assign({
                    requestId: requestId,
                    type: type,
                    recordingId: this._recordingId,
                    fileName: this._fileName,
                    mimeType: this._mimeType
                }, payload || {}), transferableArray || []);
            } catch (error) {
                clearTimeout(timerId);
                delete this._pendingRequestIdToPromise[requestId];
                reject(error);
            }
        });
    }

    _appendChunkToMemory(blobChunk) {
        this._getMemoryChunks().push(blobChunk);
        this._memoryBytesWritten = this._getMemoryBytesWritten();
    }

    _buildMemoryFile() {
        return new File(this._getMemoryChunks(), this._fileName, {
            type: this._mimeType,
            lastModified: nowTimestamp()
        });
    }

    _buildMemoryFallbackPayload() {
        this._memoryBytesWritten = this._getMemoryBytesWritten();
        return {
            totalBytesWritten: this._durableBytesWritten + this._memoryBytesWritten,
            durableBytesWritten: this._durableBytesWritten,
            memoryFallbackBytes: this._memoryBytesWritten,
            storageBackend: this._durableBytesWritten > 0 && this._lastWorkerStorageBackend ? this._lastWorkerStorageBackend : STORAGE_BACKENDS.MEMORY,
            memoryFallbackActive: this._memoryFallbackActive,
            partialRecovery: !!(this._memoryFallbackActive && this._durableBytesWritten > 0),
            memoryFallbackError: this._memoryFallbackError,
            workerInitError: this._workerInitError || null
        };
    }

    _mergePersistedFileWithMemoryChunks(persistedResponse) {
        const response = persistedResponse || {};
        const persistedFile = response.file || null;
        const persistedBytesWritten = typeof response.totalBytesWritten === "number" ? response.totalBytesWritten : (persistedFile && persistedFile.size ? persistedFile.size : 0);
        const memoryChunks = getExistingMemoryChunksForRecordingId(this._recordingId) || [];
        const storageBackend = persistedBytesWritten > 0 ?
            (response.storageBackend || this._lastWorkerStorageBackend || STORAGE_BACKENDS.MEMORY) :
            (memoryChunks.length > 0 ? STORAGE_BACKENDS.MEMORY : (response.storageBackend || this._lastWorkerStorageBackend || STORAGE_BACKENDS.MEMORY));
        const fileChunks = [];

        if (persistedFile && persistedBytesWritten > 0) {
            fileChunks.push(persistedFile.size > persistedBytesWritten ? persistedFile.slice(0, persistedBytesWritten, persistedFile.type) : persistedFile);
        }
        memoryChunks.forEach(function(memoryChunk) {
            fileChunks.push(memoryChunk);
        });

        this._durableBytesWritten = persistedBytesWritten;
        if (persistedBytesWritten > 0) {
            this._lastWorkerStorageBackend = response.storageBackend || this._lastWorkerStorageBackend;
        }

        return Object.assign({}, response, this._buildMemoryFallbackPayload(), {
            file: new File(fileChunks, this._fileName, {
                type: this._mimeType,
                lastModified: nowTimestamp()
            }),
            storageBackend: storageBackend
        });
    }

    _rememberSuccessfulPersistenceResult(appendResult) {
        if (!appendResult) {
            return appendResult;
        }

        if (appendResult.storageBackend) {
            this._lastWorkerStorageBackend = appendResult.storageBackend;
        }
        if (typeof appendResult.totalBytesWritten === "number") {
            this._durableBytesWritten = appendResult.totalBytesWritten;
        }

        return Object.assign({}, appendResult, {
            durableBytesWritten: this._durableBytesWritten,
            memoryFallbackBytes: this._memoryBytesWritten,
            memoryFallbackActive: this._memoryFallbackActive,
            partialRecovery: !!(this._memoryFallbackActive && this._durableBytesWritten > 0),
            memoryFallbackError: this._memoryFallbackError
        });
    }

    appendChunk(blobChunk) {
        this._writeQueue = this._writeQueue.then(async () => {
            if (!blobChunk || !blobChunk.size) {
                return null;
            }

            if (this._workerFatalError) {
                throw this._workerFatalError;
            }

            if (!this._worker || this._memoryFallbackActive) {
                this._appendChunkToMemory(blobChunk);
                return this._buildMemoryFallbackPayload();
            }

            const chunkBuffer = await blobChunk.arrayBuffer();
            try {
                return this._rememberSuccessfulPersistenceResult(await this._sendMessage("appendChunk", {
                    chunkBuffer: chunkBuffer
                }, [chunkBuffer]));
            } catch (error) {
                if (this._workerFatalError) {
                    throw this._workerFatalError;
                }
                this._memoryFallbackActive = true;
                this._memoryFallbackError = sanitizeError(error);
                this._appendChunkToMemory(blobChunk);
                return this._buildMemoryFallbackPayload();
            }
        });

        return this._writeQueue;
    }

    async finalize() {
        await this._writeQueue;

        if (this._workerFatalError) {
            throw this._workerFatalError;
        }

        if (!this._worker) {
            if (this._memoryFallbackActive && this._durableBytesWritten > 0) {
                throw new Error("The persisted recording prefix could not be finalized after storage fallback.");
            }

            this._memoryBytesWritten = this._getMemoryBytesWritten();
            return {
                file: this._buildMemoryFile(),
                storageBackend: STORAGE_BACKENDS.MEMORY,
                totalBytesWritten: this._memoryBytesWritten,
                durableBytesWritten: 0,
                memoryFallbackBytes: this._memoryBytesWritten,
                memoryFallbackActive: this._memoryFallbackActive,
                partialRecovery: false,
                memoryFallbackError: this._memoryFallbackError,
                workerInitError: this._workerInitError || null
            };
        }

        const finalizeResponse = await this._sendMessage("finalize");
        const finalizeMemoryChunks = getExistingMemoryChunksForRecordingId(this._recordingId);
        if (finalizeMemoryChunks && finalizeMemoryChunks.length > 0) {
            return this._mergePersistedFileWithMemoryChunks(finalizeResponse);
        }

        return this._rememberSuccessfulPersistenceResult(finalizeResponse);
    }

    async readPersistedFile() {
        if (this._workerFatalError) {
            throw this._workerFatalError;
        }

        if (!this._worker) {
            const memoryChunks = getExistingMemoryChunksForRecordingId(this._recordingId);
            if (memoryChunks && memoryChunks.length > 0) {
                return new File(memoryChunks, this._fileName, {
                    type: this._mimeType,
                    lastModified: nowTimestamp()
                });
            }
            return null;
        }

        const response = await this._sendMessage("readPersistedFile");
        const memoryChunks = getExistingMemoryChunksForRecordingId(this._recordingId);
        if (memoryChunks && memoryChunks.length > 0) {
            if (response && response.file) {
                return this._mergePersistedFileWithMemoryChunks(response).file;
            }
            return this._buildMemoryFile();
        }
        return response && response.file ? response.file : null;
    }

    async release() {
        try {
            await this._writeQueue;
        } catch (_) {
            // Ignore pending write failures during runtime release.
        }

        if (this._worker) {
            try {
                await this._sendMessage("release");
            } catch (_) {
                // Ignore worker release failures.
            }
            this._worker.terminate();
            this._worker = null;
        }
        this._memoryChunks = null;
        this._memoryBytesWritten = 0;
    }

    async clear() {
        try {
            await this._writeQueue;
        } catch (_) {
            // Ignore pending write failures during cleanup.
        }

        if (this._worker) {
            try {
                await this._sendMessage("clear");
            } catch (_) {
                // Ignore worker cleanup failures.
            }
            this._worker.terminate();
            this._worker = null;
        }
        clearMemoryChunksForRecordingId(this._recordingId);
        this._memoryChunks = null;
        this._memoryBytesWritten = 0;
    }
}

class BrowserRecordingDraftStore {
    constructor() {
        this._databasePromise = null;
    }

    canAccessPersistedDrafts() {
        if (typeof window === "undefined" || !window.indexedDB) {
            return false;
        }

        return true;
    }

    isEnabled() {
        if (!this.canAccessPersistedDrafts()) {
            return false;
        }

        return true;
    }

    getCurrentUserId() {
        try {
            const authenticatedUserInfo = LocalStorageHelper.getInstance().getAuthenticatedUserInfo();
            if (authenticatedUserInfo && authenticatedUserInfo._id) {
                return authenticatedUserInfo._id;
            }

            const anonymousSessionInfo = LocalStorageHelper.getInstance().getAnonymousBrowserSessionInfo ? LocalStorageHelper.getInstance().getAnonymousBrowserSessionInfo() : null;
            if (anonymousSessionInfo && anonymousSessionInfo.userId) {
                return anonymousSessionInfo.userId;
            }

            return LocalStorageHelper.getInstance().getRandomUserIdForUser ? LocalStorageHelper.getInstance().getRandomUserIdForUser() : null;
        } catch (error) {
            console.warn("Unable to resolve the current recording-draft user identifier", error);
            return null;
        }
    }

    _getDatabase() {
        if (!this._databasePromise) {
            this._databasePromise = new Promise((resolve, reject) => {
                if (typeof window === "undefined" || !window.indexedDB) {
                    this._databasePromise = null;
                    reject(new Error("IndexedDB is not available for browser recording draft storage."));
                    return;
                }

                const request = window.indexedDB.open(DRAFT_DB_NAME, DRAFT_DB_VERSION);
                request.onupgradeneeded = function(event) {
                    createObjectStoreIfNeeded(event.target.result);
                };
                request.onsuccess = (function(event) {
                    const database = event.target.result;
                    const resetDatabaseCache = (function() {
                        this._databasePromise = null;
                    }).bind(this);
                    const originalClose = database && database.close;

                    if (database) {
                        database.onclose = resetDatabaseCache;
                        database.onversionchange = function() {
                            resetDatabaseCache();
                            try {
                                database.close();
                            } catch (_) {
                                // Ignore close failures while resetting the handle.
                            }
                        };

                        if (typeof originalClose === "function") {
                            try {
                                database.close = function() {
                                    resetDatabaseCache();
                                    return originalClose.apply(database, arguments);
                                };
                            } catch (_) {
                                // Some browsers expose close as read-only; onclose still resets the cache.
                            }
                        }
                    }

                    resolve(database);
                }).bind(this);
                request.onerror = (function(event) {
                    this._databasePromise = null;
                    reject(event.target.error || new Error("Unable to open the browser recording draft database."));
                }).bind(this);
            });
        }

        return this._databasePromise;
    }

    async _runTransaction(storeMode, transactionCallback) {
        const database = await this._getDatabase();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction([DRAFT_MANIFEST_STORE_NAME], storeMode);
            const objectStore = transaction.objectStore(DRAFT_MANIFEST_STORE_NAME);

            let callbackResult = null;
            transaction.oncomplete = function() {
                resolve(callbackResult);
            };
            transaction.onerror = function(event) {
                reject(event.target.error || new Error("A browser recording draft transaction failed."));
            };
            transaction.onabort = function(event) {
                reject(event.target.error || new Error("A browser recording draft transaction was aborted."));
            };

            try {
                callbackResult = transactionCallback(objectStore, transaction);
            } catch (error) {
                reject(error);
            }
        });
    }

    _buildManifestDefaults(recordingId) {
        return {
            manifestVersion: DRAFT_MANIFEST_VERSION,
            recordingId: recordingId,
            status: STATUSES.RECORDING,
            storageBackend: null,
            fileName: null,
            mimeType: null,
            bytesStored: 0,
            durableBytesStored: 0,
            memoryFallbackBytes: 0,
            durationMs: 0,
            createdAt: null,
            startedAt: null,
            updatedAt: null,
            recoveryExpiresAt: null,
            expiredAt: null,
            originRoute: null,
            originFlow: null,
            userId: null,
            lastError: null,
            lastPersistenceError: null,
            lastFailureAt: null,
            uploadAttempts: 0,
            lastUploadAttemptAt: null,
            uploadCompletedAt: null,
            remoteUploadUrl: null,
            remoteUploadKey: null,
            libraryAudioId: null,
            transcriptionId: null,
            cleanupEligibleAt: null,
            dismissedAt: null,
            localBinaryClearedAt: null,
            completedAt: null,
            discardedAt: null,
            durableCompletion: null,
            lastRecoveredAt: null,
            registrationCompletedAt: null,
            registeredMedia: null,
            registrationResult: null,
            uploadedMediaParts: null,
            transcriptionStartedAt: null,
            transcriptionStartResult: null,
            usedMemoryFallback: false,
            partialRecovery: false,
            lastStorageEstimate: null,
            quotaWarningLevel: null,
            quotaExceededAt: null,
            janitorCheckedAt: null
        };
    }

    _buildAnalyticsPayload(draft, extraPayload) {
        const sourceDraft = draft || {};
        return Object.assign({
            recordingId: sourceDraft.recordingId || null,
            status: sourceDraft.status || null,
            storageBackend: sourceDraft.storageBackend || null,
            bytesStored: sourceDraft.bytesStored || 0,
            durationMs: sourceDraft.durationMs || 0,
            originRoute: sourceDraft.originRoute || null,
            attemptCount: sourceDraft.uploadAttempts || 0,
            errorCategory: sourceDraft.lastError ? sourceDraft.lastError.category || null : null,
            quotaWarningLevel: sourceDraft.quotaWarningLevel || null,
            storageUsageRatio: sourceDraft.lastStorageEstimate && typeof sourceDraft.lastStorageEstimate.usageRatio === "number" ? sourceDraft.lastStorageEstimate.usageRatio : null
        }, extraPayload || {});
    }

    _trackEvent(eventName, draft, extraPayload) {
        try {
            if (window.trebbleAnalyticsHelper && window.trebbleAnalyticsHelper.trackEvent) {
                window.trebbleAnalyticsHelper.trackEvent("recordingDurability", eventName, eventName, null, this._buildAnalyticsPayload(draft, extraPayload));
            }
        } catch (error) {
            console.warn("Unable to track the durable recording analytics event", eventName, error);
        }
    }

    async getCurrentStorageQuotaStatus() {
        try {
            const storageApi = typeof window !== "undefined" && window.navigator && window.navigator.storage ? window.navigator.storage : null;
            if (!storageApi || typeof storageApi.estimate !== "function") {
                return null;
            }

            return buildQuotaStatusFromStorageEstimate(await storageApi.estimate());
        } catch (error) {
            console.warn("Unable to estimate browser recording draft storage quota", error);
            return null;
        }
    }

    async _getStorageQuotaManifestPatch() {
        const quotaStatus = await this.getCurrentStorageQuotaStatus();
        if (!quotaStatus) {
            return {};
        }

        return {
            lastStorageEstimate: quotaStatus,
            quotaWarningLevel: quotaStatus.warningLevel || null
        };
    }

    async _getDraftFromObjectStore(objectStore, recordingId) {
        return await new Promise((resolve, reject) => {
            const request = objectStore.get(recordingId);
            request.onsuccess = function() {
                resolve(cloneDraft(request.result));
            };
            request.onerror = function(event) {
                reject(event.target.error || new Error("Unable to retrieve the recording draft manifest."));
            };
        });
    }

    async getDraft(recordingId) {
        if (!this.canAccessPersistedDrafts() || !recordingId) {
            return null;
        }

        return await this._runTransaction("readonly", (objectStore) => {
            return this._getDraftFromObjectStore(objectStore, recordingId);
        });
    }

    async getDraftForCurrentUser(recordingId) {
        const draft = await this.getDraft(recordingId);
        const currentUserId = this.getCurrentUserId();
        if (!shouldDisplayRecordingDraftForUser(draft, currentUserId)) {
            return null;
        }

        return draft;
    }

    async _putDraftManifest(draft) {
        return await this._runTransaction("readwrite", (objectStore) => {
            return new Promise((resolve, reject) => {
                const normalizedDraft = cloneDraft(draft);
                const request = objectStore.put(normalizedDraft);
                request.onsuccess = function() {
                    resolve(normalizedDraft);
                };
                request.onerror = function(event) {
                    reject(event.target.error || new Error("Unable to persist the recording draft manifest."));
                };
            });
        });
    }

    async createDraft(options) {
        if (!this.isEnabled() || !options || !options.recordingId) {
            return null;
        }

        const existingDraft = await this.getDraft(options.recordingId);
        const currentTimestamp = nowTimestamp();
        const quotaManifestPatch = await this._getStorageQuotaManifestPatch();
        const nextDraft = Object.assign(
            this._buildManifestDefaults(options.recordingId),
            existingDraft || {},
            quotaManifestPatch,
            {
                manifestVersion: DRAFT_MANIFEST_VERSION,
                recordingId: options.recordingId,
                status: STATUSES.RECORDING,
                fileName: options.fileName || (existingDraft && existingDraft.fileName) || options.recordingId,
                mimeType: options.mimeType || (existingDraft && existingDraft.mimeType) || null,
                bytesStored: existingDraft && existingDraft.bytesStored ? existingDraft.bytesStored : 0,
                durationMs: existingDraft && existingDraft.durationMs ? existingDraft.durationMs : 0,
                createdAt: existingDraft && existingDraft.createdAt ? existingDraft.createdAt : currentTimestamp,
                startedAt: existingDraft && existingDraft.startedAt ? existingDraft.startedAt : currentTimestamp,
                updatedAt: currentTimestamp,
                recoveryExpiresAt: currentTimestamp + RECOVERABLE_DRAFT_MAX_AGE_IN_MS,
                expiredAt: null,
                originRoute: options.originRoute || (existingDraft && existingDraft.originRoute) || null,
                originFlow: options.originFlow || (existingDraft && existingDraft.originFlow) || null,
                userId: options.userId || (existingDraft && existingDraft.userId) || this.getCurrentUserId(),
                lastError: null,
                lastFailureAt: null,
                dismissedAt: null
            }
        );

        const persistedDraft = await this._putDraftManifest(nextDraft);
        this._trackEvent("DraftCreated", persistedDraft);
        return persistedDraft;
    }

    async patchDraft(recordingId, patch, options) {
        if (!this.canAccessPersistedDrafts() || !recordingId) {
            return null;
        }

        const patchOptions = options || {};
        return await this._runTransaction("readwrite", (objectStore) => {
            return new Promise((resolve, reject) => {
                const getRequest = objectStore.get(recordingId);
                getRequest.onsuccess = function() {
                    const existingDraft = cloneDraft(getRequest.result);
                    if (!existingDraft && !patchOptions.createIfMissing) {
                        resolve(null);
                        return;
                    }

                    const currentTimestamp = patchOptions.updatedAt || nowTimestamp();
                    const nextDraft = Object.assign(
                        this._buildManifestDefaults(recordingId),
                        existingDraft || {},
                        patch || {},
                        {
                            manifestVersion: DRAFT_MANIFEST_VERSION,
                            recordingId: recordingId,
                            updatedAt: currentTimestamp
                        }
                    );

                    if (patchOptions.clearDismissedAt) {
                        nextDraft.dismissedAt = null;
                    }
                    if (patchOptions.clearLastError) {
                        nextDraft.lastError = null;
                    }

                    const normalizedDraft = cloneDraft(nextDraft);
                    const putRequest = objectStore.put(normalizedDraft);
                    putRequest.onsuccess = function() {
                        resolve(normalizedDraft);
                    };
                    putRequest.onerror = function(event) {
                        reject(event.target.error || new Error("Unable to persist the recording draft manifest."));
                    };
                }.bind(this);
                getRequest.onerror = function(event) {
                    reject(event.target.error || new Error("Unable to retrieve the recording draft manifest."));
                };
            });
        });
    }

    async recordChunkPersisted(recordingId, chunkInfo) {
        if (!this.isEnabled() || !recordingId || !chunkInfo) {
            return null;
        }

        return await this.patchDraft(recordingId, {
            storageBackend: chunkInfo.storageBackend || null,
            bytesStored: chunkInfo.totalBytesWritten || 0,
            durableBytesStored: typeof chunkInfo.durableBytesWritten === "number" ? chunkInfo.durableBytesWritten : (chunkInfo.totalBytesWritten || 0),
            memoryFallbackBytes: chunkInfo.memoryFallbackBytes || 0,
            usedMemoryFallback: chunkInfo.memoryFallbackActive === true,
            partialRecovery: chunkInfo.partialRecovery === true,
            lastPersistenceError: chunkInfo.memoryFallbackError || null,
            durationMs: chunkInfo.durationMs || 0
        }, {
            clearDismissedAt: true
        });
    }

    async markRecordingStopping(recordingId, extraInfo) {
        return await this.patchDraft(recordingId, Object.assign({
            status: STATUSES.STOPPING
        }, extraInfo || {}), {
            clearDismissedAt: true
        });
    }

    async markStoppedReadyToUpload(recordingId, extraInfo) {
        const quotaManifestPatch = await this._getStorageQuotaManifestPatch();
        return await this.patchDraft(recordingId, Object.assign({
            status: STATUSES.STOPPED_READY_TO_UPLOAD,
            lastError: null
        }, quotaManifestPatch, extraInfo || {}), {
            clearDismissedAt: true
        });
    }

    async markUploadStarted(recordingId) {
        const existingDraft = await this.getDraft(recordingId);
        const currentTimestamp = nowTimestamp();
        return await this.patchDraft(recordingId, {
            status: STATUSES.UPLOADING,
            uploadAttempts: existingDraft && existingDraft.uploadAttempts ? existingDraft.uploadAttempts + 1 : 1,
            lastUploadAttemptAt: currentTimestamp,
            lastError: null
        }, {
            clearDismissedAt: true
        }).then((function(updatedDraft) {
            this._trackEvent("UploadStarted", updatedDraft);
            return updatedDraft;
        }).bind(this));
    }

    async markUploadCompleted(recordingId, uploadedMediaParts) {
        const partsArray = Array.isArray(uploadedMediaParts) ? uploadedMediaParts.slice() : (uploadedMediaParts ? [uploadedMediaParts] : []);
        const primaryUploadPart = partsArray.length > 0 ? partsArray[0] : null;

        return await this.patchDraft(recordingId, {
            status: STATUSES.UPLOADED_UNREGISTERED,
            remoteUploadUrl: primaryUploadPart && primaryUploadPart.uri ? primaryUploadPart.uri : null,
            remoteUploadKey: primaryUploadPart && primaryUploadPart.locationKey ? primaryUploadPart.locationKey : null,
            uploadedMediaParts: partsArray,
            uploadCompletedAt: nowTimestamp(),
            lastError: null
        }, {
            clearDismissedAt: true
        }).then((function(updatedDraft) {
            this._trackEvent("UploadCompleted", updatedDraft);
            return updatedDraft;
        }).bind(this));
    }

    async markRegistrationCompleted(recordingId, registeredMedia) {
        return await this.patchDraft(recordingId, {
            status: STATUSES.REGISTERED_UNTRANSCRIBED,
            libraryAudioId: registeredMedia && registeredMedia.audioId ? registeredMedia.audioId : null,
            registrationCompletedAt: nowTimestamp(),
            registeredMedia: registeredMedia || null,
            registrationResult: registeredMedia || null,
            lastError: null
        }, {
            clearDismissedAt: true
        }).then((function(updatedDraft) {
            this._trackEvent("RegistrationCompleted", updatedDraft);
            return updatedDraft;
        }).bind(this));
    }

    async markTranscriptionStarted(recordingId, transcriptionStartResult) {
        return await this.patchDraft(recordingId, {
            status: STATUSES.TRANSCRIBING,
            transcriptionId: transcriptionStartResult && transcriptionStartResult.transcriptionId ? transcriptionStartResult.transcriptionId : null,
            transcriptionStartedAt: nowTimestamp(),
            transcriptionStartResult: transcriptionStartResult || null,
            lastError: null
        }, {
            clearDismissedAt: true
        }).then((function(updatedDraft) {
            this._trackEvent("TranscriptionStarted", updatedDraft);
            return updatedDraft;
        }).bind(this));
    }

    async markCompleted(recordingId, extraInfo) {
        const currentTimestamp = nowTimestamp();
        return await this.patchDraft(recordingId, Object.assign({
            status: STATUSES.COMPLETED,
            completedAt: currentTimestamp,
            cleanupEligibleAt: currentTimestamp + COMPLETED_DRAFT_LOCAL_BINARY_RETENTION_IN_MS,
            durableCompletion: {
                savedOutcome: true,
                completedAt: currentTimestamp,
                localBinaryRetentionUntil: currentTimestamp + COMPLETED_DRAFT_LOCAL_BINARY_RETENTION_IN_MS
            },
            recoveryExpiresAt: null,
            lastError: null
        }, extraInfo || {}));
    }

    async markRecoverableFailure(recordingId, error, extraInfo) {
        const sanitizedError = sanitizeError(error);
        const currentTimestamp = nowTimestamp();
        const quotaManifestPatch = await this._getStorageQuotaManifestPatch();
        return await this.patchDraft(recordingId, Object.assign({
            status: STATUSES.FAILED_RECOVERABLE,
            lastError: sanitizedError,
            lastFailureAt: currentTimestamp,
            quotaExceededAt: sanitizedError && sanitizedError.quotaExceeded ? currentTimestamp : null
        }, quotaManifestPatch, extraInfo || {})).then((function(updatedDraft) {
            this._trackEvent("RecoveryFailed", updatedDraft, {
                errorCategory: getErrorCategory(error)
            });
            return updatedDraft;
        }).bind(this));
    }

    async markRecovered(recordingId) {
        const existingDraft = await this.getDraft(recordingId);
        if (existingDraft && !shouldDisplayRecordingDraftForUser(existingDraft, this.getCurrentUserId())) {
            return existingDraft;
        }

        return await this.patchDraft(recordingId, {
            dismissedAt: null,
            lastError: null,
            lastRecoveredAt: nowTimestamp()
        }).then((function(updatedDraft) {
            this._trackEvent("DraftRecovered", updatedDraft);
            return updatedDraft;
        }).bind(this));
    }

    async dismissDraft(recordingId) {
        return await this.patchDraft(recordingId, {
            dismissedAt: nowTimestamp()
        });
    }

    async dismissDrafts(recordingIds) {
        const ids = Array.isArray(recordingIds) ? recordingIds : [];
        return await Promise.all(ids.map((function(recordingId) {
            return this.dismissDraft(recordingId);
        }).bind(this)));
    }

    async markDiscarded(recordingId) {
        const existingDraft = await this.getDraft(recordingId);
        if (existingDraft && !shouldDisplayRecordingDraftForUser(existingDraft, this.getCurrentUserId())) {
            return existingDraft;
        }

        const currentTimestamp = nowTimestamp();
        return await this.patchDraft(recordingId, {
            status: STATUSES.DISCARDED,
            dismissedAt: currentTimestamp,
            discardedAt: currentTimestamp,
            cleanupEligibleAt: currentTimestamp,
            recoveryExpiresAt: null,
            lastError: null
        });
    }

    async getPersistedBinaryFile(recordingId, fallbackInfo) {
        if (!this.canAccessPersistedDrafts() || !recordingId) {
            return null;
        }

        const draft = await this.getDraftForCurrentUser(recordingId);
        if (!draft) {
            return null;
        }

        const binaryStoreClient = new BrowserRecordingBinaryStoreClient({
            recordingId: recordingId,
            fileName: draft && draft.fileName ? draft.fileName : (fallbackInfo && fallbackInfo.fileName) || recordingId,
            mimeType: draft && draft.mimeType ? draft.mimeType : (fallbackInfo && fallbackInfo.mimeType) || "application/octet-stream"
        });

        try {
            return await binaryStoreClient.readPersistedFile();
        } finally {
            await binaryStoreClient.release();
        }
    }

    async deletePersistedBinary(recordingId, options) {
        const cleanupOptions = options || {};
        if (!recordingId) {
            return null;
        }

        const draft = await this.getDraft(recordingId);
        const currentUserId = this.getCurrentUserId();
        if (draft && !shouldDisplayRecordingDraftForUser(draft, currentUserId)) {
            return draft;
        }

        const binaryStoreClient = new BrowserRecordingBinaryStoreClient({
            recordingId: recordingId,
            fileName: draft && draft.fileName ? draft.fileName : recordingId,
            mimeType: draft && draft.mimeType ? draft.mimeType : "application/octet-stream"
        });

        try {
            await binaryStoreClient.clear();
        } finally {
            await binaryStoreClient.release();
        }

        if (cleanupOptions.patchManifest === false || !this.canAccessPersistedDrafts()) {
            return draft;
        }

        return await this.patchDraft(recordingId, {
            storageBackend: STORAGE_BACKENDS.CLEARED,
            bytesStored: 0,
            localBinaryClearedAt: nowTimestamp(),
            dismissedAt: cleanupOptions.dismissedAt !== false ? nowTimestamp() : null
        });
    }

    async cleanupLocalBinaryIfEligible(recordingId, options) {
        if (!this.canAccessPersistedDrafts() || !recordingId) {
            return null;
        }

        const cleanupOptions = options || {};
        const draft = await this.getDraft(recordingId);
        if (cleanupOptions.requireCurrentUser !== false && !shouldDisplayRecordingDraftForUser(draft, this.getCurrentUserId())) {
            return draft;
        }

        if (!canCleanupLocalBinaryForDraft(draft, cleanupOptions.now)) {
            return draft;
        }

        await this.deletePersistedBinary(recordingId, {
            patchManifest: false
        });

        const cleanedDraft = await this.patchDraft(recordingId, {
            storageBackend: STORAGE_BACKENDS.CLEARED,
            bytesStored: 0,
            localBinaryClearedAt: nowTimestamp(),
            dismissedAt: nowTimestamp()
        });
        this._trackEvent("LocalCleanupCompleted", cleanedDraft);
        return cleanedDraft;
    }

    async listDraftsForCurrentUser() {
        if (!this.canAccessPersistedDrafts()) {
            return [];
        }

        const currentUserId = this.getCurrentUserId();
        if (!currentUserId) {
            return [];
        }

        return await this._runTransaction("readonly", (objectStore) => {
            return new Promise((resolve, reject) => {
                const index = objectStore.index("userId");
                const request = index.getAll(currentUserId);
                request.onsuccess = function() {
                    const results = (request.result || []).map(cloneDraft).filter(function(draft) {
                        return shouldDisplayRecordingDraftForUser(draft, currentUserId);
                    });
                    resolve(results.sort(function(firstDraft, secondDraft) {
                        return (secondDraft.updatedAt || 0) - (firstDraft.updatedAt || 0);
                    }));
                };
                request.onerror = function(event) {
                    reject(event.target.error || new Error("Unable to list the browser recording drafts for the active user."));
                };
            });
        });
    }

    async listRecoverableDraftsForCurrentUser(options) {
        const listOptions = options || {};
        const currentUserId = this.getCurrentUserId();
        const currentTimestamp = listOptions.now || nowTimestamp();
        const includeDismissed = listOptions.includeDismissed !== false;
        const includeBestEffortActiveStates = listOptions.includeBestEffortActiveStates === true;
        const drafts = await this.listDraftsForCurrentUser();

        return drafts.filter(function(draft) {
            if (!shouldDisplayRecordingDraftForUser(draft, currentUserId)) {
                return false;
            }

            if (isDraftExpiredForRecovery(draft, currentTimestamp)) {
                return false;
            }

            if (includeBestEffortActiveStates) {
                if (!isRecordingDraftRecoverable(draft)) {
                    return false;
                }
            } else if (!isRecordingDraftUserRecoverable(draft)) {
                return false;
            }

            if (!includeDismissed && shouldSuppressDraftRecoveryPrompt(draft, currentTimestamp, RECOVERY_PROMPT_DISMISSAL_TTL_IN_MS)) {
                return false;
            }

            return true;
        });
    }

    async runConservativeJanitorForCurrentUser(options) {
        if (!this.canAccessPersistedDrafts()) {
            return {
                checked: 0,
                expired: 0,
                localBinariesCleared: 0,
                skipped: 0
            };
        }

        const janitorOptions = options || {};
        const currentTimestamp = janitorOptions.now || nowTimestamp();
        const currentUserId = this.getCurrentUserId();
        const summary = {
            checked: 0,
            expired: 0,
            localBinariesCleared: 0,
            skipped: 0
        };

        if (!currentUserId) {
            return summary;
        }

        const drafts = await this.listDraftsForCurrentUser();
        for (let index = 0; index < drafts.length; index++) {
            const draft = drafts[index];
            if (!draft || !draft.recordingId || !shouldDisplayRecordingDraftForUser(draft, currentUserId)) {
                summary.skipped = summary.skipped + 1;
                continue;
            }

            summary.checked = summary.checked + 1;
            let latestDraft = draft;

            if ((isDraftExpiredForRecovery(latestDraft, currentTimestamp) || isExpiredTombstoneDraft(latestDraft, currentTimestamp)) && !latestDraft.expiredAt) {
                latestDraft = await this.patchDraft(latestDraft.recordingId, {
                    expiredAt: currentTimestamp,
                    dismissedAt: currentTimestamp,
                    janitorCheckedAt: currentTimestamp
                });
                summary.expired = summary.expired + 1;
            }

            if (canCleanupLocalBinaryForDraft(latestDraft, currentTimestamp)) {
                const cleanedDraft = await this.cleanupLocalBinaryIfEligible(latestDraft.recordingId, {
                    now: currentTimestamp,
                    requireCurrentUser: true
                });
                if (cleanedDraft && cleanedDraft.localBinaryClearedAt) {
                    summary.localBinariesCleared = summary.localBinariesCleared + 1;
                }
            }
        }

        return summary;
    }

    getRecoveryPromptDismissalTtlInMs() {
        return RECOVERY_PROMPT_DISMISSAL_TTL_IN_MS;
    }
}

const BrowserRecordingDraftStoreInstance = new BrowserRecordingDraftStore();
if (typeof window !== "undefined") {
    window.BrowserRecordingDraftStore = BrowserRecordingDraftStoreInstance;
}

export {
    BrowserRecordingBinaryStoreClient,
    STORAGE_BACKENDS,
    STATUSES,
    RECOVERABLE_STATUSES,
    USER_VISIBLE_RECOVERABLE_STATUSES,
    CLEANUP_ELIGIBLE_STATUSES,
    DRAFT_MANIFEST_VERSION,
    RECOVERABLE_DRAFT_MAX_AGE_IN_MS,
    COMPLETED_DRAFT_LOCAL_BINARY_RETENTION_IN_MS,
    isRecordingDraftRecoverable,
    isRecordingDraftUserRecoverable,
    canCleanupLocalBinaryForDraft,
    shouldSuppressDraftRecoveryPrompt,
    shouldDisplayRecordingDraftForUser,
    hasExplicitDurableCompletionMetadata,
    isDraftExpiredForRecovery,
    isExpiredTombstoneDraft,
    hasRecoverableLocalBinary,
    hasRemoteRecoveryCheckpoint,
    isStatusAtOrBeyond,
    sanitizeError,
    getErrorCategory,
    getHttpFailureCategory,
    isQuotaExceededError,
    buildQuotaStatusFromStorageEstimate
};

export default {
    getInstance: function() {
        return BrowserRecordingDraftStoreInstance;
    },
    BrowserRecordingBinaryStoreClient: BrowserRecordingBinaryStoreClient,
    STORAGE_BACKENDS: STORAGE_BACKENDS,
    STATUSES: STATUSES,
    RECOVERABLE_STATUSES: RECOVERABLE_STATUSES,
    USER_VISIBLE_RECOVERABLE_STATUSES: USER_VISIBLE_RECOVERABLE_STATUSES,
    CLEANUP_ELIGIBLE_STATUSES: CLEANUP_ELIGIBLE_STATUSES,
    DRAFT_MANIFEST_VERSION: DRAFT_MANIFEST_VERSION,
    RECOVERABLE_DRAFT_MAX_AGE_IN_MS: RECOVERABLE_DRAFT_MAX_AGE_IN_MS,
    COMPLETED_DRAFT_LOCAL_BINARY_RETENTION_IN_MS: COMPLETED_DRAFT_LOCAL_BINARY_RETENTION_IN_MS,
    isRecordingDraftRecoverable: isRecordingDraftRecoverable,
    isRecordingDraftUserRecoverable: isRecordingDraftUserRecoverable,
    canCleanupLocalBinaryForDraft: canCleanupLocalBinaryForDraft,
    shouldSuppressDraftRecoveryPrompt: shouldSuppressDraftRecoveryPrompt,
    shouldDisplayRecordingDraftForUser: shouldDisplayRecordingDraftForUser,
    hasExplicitDurableCompletionMetadata: hasExplicitDurableCompletionMetadata,
    isDraftExpiredForRecovery: isDraftExpiredForRecovery,
    isExpiredTombstoneDraft: isExpiredTombstoneDraft,
    hasRecoverableLocalBinary: hasRecoverableLocalBinary,
    hasRemoteRecoveryCheckpoint: hasRemoteRecoveryCheckpoint,
    isStatusAtOrBeyond: isStatusAtOrBeyond,
    sanitizeError: sanitizeError,
    getErrorCategory: getErrorCategory,
    getHttpFailureCategory: getHttpFailureCategory,
    isQuotaExceededError: isQuotaExceededError,
    buildQuotaStatusFromStorageEstimate: buildQuotaStatusFromStorageEstimate
};
